import { COLUMN_PRESETS } from "./schema.js";

function uid(prefix = "id") {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

// Row builders used only by demo-mode fallbacks and non-persisted local sheets.
// Persisted dashboard actions use server responses.
export function buildMockCompanyRow(name, domain, industry) {
  return { _id: uid("row"), name, domain, industry };
}

export function buildMockEmployeeRow(name, title, company, company_domain) {
  return { _id: uid("row"), name, title, company, company_domain };
}

export function buildMockUpdateRow(company, kind, title, published) {
  return { _id: uid("row"), company, kind, title, published };
}

// Marketing-page demo seed. These sheet ids match chip data attributes on
// /prospector and / landing pages.
export function buildDemoSheets() {
  return [
    {
      id: "sheet-stripe-customers",
      name: "Stripe's customers",
      entityType: "companies",
      parentId: null,
      origin: "Demo: companies that use Stripe",
      columns: [...COLUMN_PRESETS.companies],
      rows: synthesizeTransformedRows("companies", null),
    },
    {
      id: "sheet-stripe-comp",
      name: "Stripe's competitors",
      entityType: "companies",
      parentId: null,
      origin: "Demo: head-to-head competitors of Stripe",
      columns: [...COLUMN_PRESETS.companies],
      rows: synthesizeTransformedRows("companies", null),
    },
    {
      id: "sheet-stripe-customer-employees",
      name: "Decision-makers & emails",
      entityType: "employees",
      parentId: null,
      origin: "Demo: decision-makers at Stripe-ecosystem companies",
      columns: [...COLUMN_PRESETS.employees],
      rows: synthesizeTransformedRows("employees", null),
    },
  ];
}

// Lightweight CSV parser. Handles quoted fields and escaped quotes; this is
// demo-only, not the persisted dashboard import path.
export function parseCSV(text, entityType) {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length === 0) return [];
  const records = lines.map(line => {
    const out = [];
    let cur = "";
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQuote) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') inQuote = false;
        else cur += c;
      } else {
        if (c === '"') inQuote = true;
        else if (c === ",") { out.push(cur); cur = ""; }
        else cur += c;
      }
    }
    out.push(cur);
    return out.map(s => s.trim());
  });
  const first = records[0][0]?.toLowerCase();
  const headerWords = ["domain", "url", "website", "name", "company", "email"];
  const dataRows = headerWords.includes(first) ? records.slice(1) : records;

  return dataRows.map(cells => {
    if (entityType === "companies") {
      const dom = (cells[0] || "").replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
      const name = cells[1] || (dom ? dom.split(".")[0].replace(/^./, c => c.toUpperCase()) : "");
      const industry = cells[2] || "";
      return buildMockCompanyRow(name, dom, industry);
    }
    const name = cells[0] || "";
    const dom = (cells[1] || "").replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
    const company = dom ? dom.split(".")[0].replace(/^./, c => c.toUpperCase()) : (cells[2] || "");
    const title = cells[2] || "";
    return buildMockEmployeeRow(name, title, company, dom);
  });
}

export function synthesizeTransformedRows(yields, source) {
  if (yields === "companies") {
    const pool = [
      ["Plaid", "plaid.com", "Fintech infra"],
      ["Brex", "brex.com", "Corporate cards"],
      ["Ramp", "ramp.com", "Spend management"],
      ["Mercury", "mercury.com", "Banking"],
      ["Modern Treasury", "moderntreasury.com", "Payment ops"],
      ["Unit", "unit.co", "Banking infra"],
      ["Pipe", "pipe.com", "Capital markets"],
      ["Wise", "wise.com", "Cross-border"],
      ["Revolut", "revolut.com", "Neobank"],
      ["Chime", "chime.com", "Neobank"],
      ["Robinhood", "robinhood.com", "Brokerage"],
      ["Coinbase", "coinbase.com", "Crypto"],
    ];
    return shuffle(pool).slice(0, 8).map(([n, d, i]) => buildMockCompanyRow(n, d, i));
  }
  if (yields === "employees") {
    const pool = [
      ["Patrick Collison", "Co-founder & CEO", "Stripe", "stripe.com"],
      ["John Collison", "Co-founder & President", "Stripe", "stripe.com"],
      ["Henrique Dubugras", "Co-founder & CEO", "Brex", "brex.com"],
      ["Pedro Franceschi", "Co-founder & CEO", "Brex", "brex.com"],
      ["Eric Glyman", "CEO", "Ramp", "ramp.com"],
      ["Karim Atiyeh", "CTO", "Ramp", "ramp.com"],
      ["Immad Akhund", "CEO", "Mercury", "mercury.com"],
      ["Zach Perret", "CEO", "Plaid", "plaid.com"],
      ["William Hockey", "Co-founder", "Plaid", "plaid.com"],
      ["Dimitri Dadiomov", "CEO", "Modern Treasury", "moderntreasury.com"],
    ];
    return shuffle(pool).slice(0, 6).map(([n, t, c, d]) => buildMockEmployeeRow(n, t, c, d));
  }
  if (yields === "updates") {
    const sourceName = (source && source.rows[0] && source.rows[0].name) || "Stripe";
    const sourceDom  = (source && source.rows[0] && source.rows[0].domain) || "stripe.com";
    const today = new Date();
    const rows = [];
    const titles = [
      ["Blog",    `Why we built ${sourceName} for the next decade of payments`],
      ["X post",  `Quick thread on how ${sourceDom} is rethinking onboarding`],
      ["Website", "Updated /pricing page"],
      ["Blog",    "Engineering deep dive: latency reductions in our API"],
      ["X post",  "Thrilled to announce our latest customer win"],
      ["Blog",    "What we learned from 1B API requests last quarter"],
    ];
    titles.forEach(([k, t], i) => {
      const d = new Date(today);
      d.setDate(d.getDate() - (i * 3 + 1));
      rows.push(buildMockUpdateRow(sourceName, k, t, d.toISOString().slice(0, 10)));
    });
    return rows;
  }
  return [];
}

function shuffle(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}
